// The envelope's looks (sprint 28 item 3, roadmaps 63 and 64), one-bit pixel art in the art bible (studio/ART-BIBLE.md): ink, paper and,
// on a gilded round only, the one gold. Every sprite is drawn here as a grid of art pixels (pure functions, tested in
// test/mail-art.test.js) and handed to client/mail.css as an SVG data URL in a CSS variable, one rect run per row, crisp edges, shown at
// a whole number of CSS px per art px (2, the stage's own, or 3 zoomed in) so there is never a grey pixel. mail.js (the beats and the wiring) does not change.
//
// Markup contract, what mail.js reads and sets:
//   root #mail  data-state  idle | seep | slide | peephole | shake | zoom | result    data-tier  points | shield | big | jackpot
//               data-flash "1" for 120 ms (the one flash)   data-tray "1" while the tray is shown   data-gold "1" on a gilded round
//   .mail-lamp .mail-keyring .mail-shield .mail-last   the tray (mail.js writes their words; the lamp and keyring keep a data-step and
//                                                       their icon in step with those words, see `mirror` below)
//   .mail-stage .mail-hole (the peephole) .mail-paper (the envelope) .mail-result (the words) .mail-hint (the skip hint)
// The stage, drawn here: .mail-flash (the room's one flash, behind the window), .mail-burst (the big and gilded results' rays, out past
// the window), .mail-title (striped bar), .mail-scene > .mail-zoomrect (the Finder-style zoom rectangles) and .mail-view (the
// bottom of the door and the lamp-lit floor; zoomed to 3 px per art px by background size, never a transform) > .mail-gold (gilded light under the door), .mail-rays (the light behind
// the opened envelope: a pool, rays, or gold rays), .mail-paper, .mail-hole, .mail-flicker (the light dipping during the held shake). The tray adds .mail-mark (the big
// or gilded mark stamped on the keyring while the result is up).
import { ACCENTS } from './bits.js';

export const ART_PX = 2; // CSS px per art pixel: the stage's own 2x
export const VIEW = { w: 148, h: 84 }; // the scene through the window, art px
// the closed envelope lying on the floor (view coordinates): every tier's sprite is this box; a plain one (points, shield) is 36 x 22 in
// its bottom middle, a big or gilded one fills it (44 x 28), so the rare tiers come under the door visibly larger and heavier
export const ENV = { x: 52, y: 45, w: 44, h: 28 };
export const SMALL = { dx: 4, dy: 6, w: 36, h: 22 }; // the plain envelope inside ENV
export const OPEN = { x: 56, y: 33, w: 36, h: 40 }; // the opened envelope, the letter (or shield) half out above it
export const LENS = { cx: 74, cy: 62, r: 24 }; // the peephole, centred on the envelope
export const LENS_BIG = 28; // the big and gilded envelopes need a wider lens
export const IRIS = [40, 20, 8]; // the iris closes in three drawn frames: the lens this much wider, then the lens itself
// The zoom never resamples (studio/ART-BIBLE.md, "whole pixels"): the view is shown at a whole number of CSS px per art px, 2 (as
// laid out) or 3 (zoomed), by background size and position in whole px, never by a transform. ZOOM: the zoomed view, offset so the
// opened envelope sits above the words. place(): where an art box lands, in CSS px, at p px per art px.
export const ZOOM = { p: 3, x: -74, y: -96 };
export const place = (box, p = ART_PX, ox = 0, oy = 0) => ({ left: ox + box.x * p, top: oy + box.y * p, width: box.w * p, height: box.h * p });
// The window (mail.css): its padding box is 296 wide (the scene) and 192 high (the title bar 22 + its rule 2, the scene 168); the words
// lie over the bottom of the scene. The big and gilded results burst out of it: BURST is each burst's box in CSS px from the padding box's
// top left; the window itself (with its 2 px border) covers x -2..298, y -2..194 of that. Gilded is the biggest.
export const WIN = { w: 296, h: 192, title: 24 };
// On a 390 px phone the gilded box is the whole width, from under the menu bar down to just above the door's sum; the big one is smaller
// and its rays stop short of the edges.
export const BURST = { big: { x: -42, y: -26, w: 380, h: 228, reach: [1, 0.9, 0.76] }, jackpot: { x: -47, y: -36, w: 390, h: 244, reach: [1, 1, 0.9] } };
export const ICON = 12, MARK = 12; // tray icons and marks, art px
export const STEPS = 10; // the lamp's steps and the keyring's fragments
export const INK = 1, PAPER = 2, GOLD = 3; // 0 = transparent
export const FILLS = { [INK]: '#000', [PAPER]: '#fff', [GOLD]: ACCENTS.gold };

// ---------- the pixel grid ----------
const B8 = (() => { let m = [[0]], n = 1; while (n < 8) { const N = []; for (let y = 0; y < n * 2; y++) { N[y] = []; for (let x = 0; x < n * 2; x++) N[y][x] = m[y % n][x % n] * 4 + [[0, 2], [3, 1]][(y / n) | 0][(x / n) | 0]; } m = N; n *= 2; } return m; })();
export const lit = (x, y, v) => v * 64 > B8[y & 7][x & 7] + 0.5; // Bayer 8x8: is this pixel paper at brightness v (0..1)?

export function grid(w, h) {
  const g = new Uint8Array(w * h);
  const set = (x, y, v) => { x = Math.round(x); y = Math.round(y); if (x >= 0 && y >= 0 && x < w && y < h) g[y * w + x] = v; };
  const get = (x, y) => (x >= 0 && y >= 0 && x < w && y < h ? g[y * w + x] : 0);
  const rect = (x, y, rw, rh, v) => { for (let j = y; j < y + rh; j++) for (let i = x; i < x + rw; i++) set(i, j, v); };
  const box = (x, y, rw, rh, v = INK) => { rect(x, y, rw, 1, v); rect(x, y + rh - 1, rw, 1, v); rect(x, y, 1, rh, v); rect(x + rw - 1, y, 1, rh, v); };
  const line = (x0, y0, x1, y1, v = INK) => { // Bresenham, whole pixels
    const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    for (;;) { set(x0, y0, v); if (x0 === x1 && y0 === y1) break; const e2 = 2 * err; if (e2 >= dy) { err += dy; x0 += sx; } if (e2 <= dx) { err += dx; y0 += sy; } }
  };
  const each = fn => { for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) { const v = fn(x, y, g[y * w + x]); if (v !== undefined) g[y * w + x] = v; } };
  return { w, h, g, set, get, rect, box, line, each };
}

export function toSvg(d) {
  const runs = { [INK]: [], [PAPER]: [], [GOLD]: [] };
  for (let y = 0; y < d.h; y++) for (let x = 0; x < d.w;) { const v = d.g[y * d.w + x]; let e = x; while (e < d.w && d.g[y * d.w + e] === v) e++; if (v) runs[v].push(`M${x} ${y}h${e - x}v1h-${e - x}z`); x = e; }
  const paths = [PAPER, GOLD, INK].filter(v => runs[v].length).map(v => `<path fill="${FILLS[v]}" d="${runs[v].join('')}"/>`).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${d.w}" height="${d.h}" viewBox="0 0 ${d.w} ${d.h}" shape-rendering="crispEdges">${paths}</svg>`;
}
export const svgUrl = d => `url("data:image/svg+xml,${encodeURIComponent(toSvg(d))}")`;

// ---------- the scene: the bottom of the door, the gap, the sill and the lamp-lit floor ----------
export const GAP = { top: 42, sill: 45 }; // rows: the door's lit bottom edge is row 41, the gap 42..44, the sill 45..46, the floor from 48
export function scene() {
  const d = grid(VIEW.w, VIEW.h), k = 1; // k: the lamp's strength
  d.each((x, y) => {
    if (y <= 44) {
      if (x < 6 || x > 141) return lit(x, y, 0.06) ? PAPER : INK; // the hall wall
      if (x < 9 || x > 138) return (x === 6 || x === 141) || ((x === 8 || x === 139) && y % 2 === 0) ? PAPER : INK; // the frame
      if (y > 41) return INK; // the gap under the door
      if (y === 41) return PAPER; // the door's bottom edge catches the lamp
      const lift = 0.42 * Math.max(0, 1 - Math.hypot((x - 74) / 76, (y - 50) / 34)) * k;
      const grain = (x * 7 + Math.floor(Math.sin(y * 0.05 + x) * 3)) % 11 === 0 ? 0.2 : 0;
      return lit(x, y, 0.05 + lift + grain) ? PAPER : INK; // dark wood lit from below, its grain
    }
    if (y === 45) return x > 2 && x < 145 ? PAPER : INK; // the sill
    if (y === 46) return x > 2 && x < 145 && x % 2 === 0 ? PAPER : INK;
    if (y === 47) return INK;
    if ((y - 48) % 7 === 6) return INK; // the gaps between floorboards
    const row = Math.floor((y - 48) / 7);
    if ((x + row * 23) % 41 === 0) return INK; // board ends, staggered
    const pool = Math.max(0, 1 - Math.hypot((x - 74) / 84, (y - 63) / 30));
    return lit(x, y, (0.06 + 0.9 * Math.pow(pool, 0.8)) * k) ? PAPER : INK; // the lamp's pool on the boards
  });
  // the kick plate on the door: a paper outline, a dull metal face, four screws
  for (let y = 27; y <= 37; y++) for (let x = 31; x <= 116; x++) d.set(x, y, lit(x, y, 0.4 * k) ? PAPER : INK);
  d.box(30, 26, 88, 13, PAPER); d.rect(31, 27, 86, 1, INK);
  for (const [x, y] of [[33, 29], [114, 29], [33, 35], [114, 35]]) { d.rect(x - 1, y - 1, 3, 3, INK); d.set(x, y, PAPER); }
  return d;
}

// the gold that seeps under the door on a gilded round: in the gap, then fanning out over the floor's dark pixels
export function seep(base = scene()) {
  const d = grid(VIEW.w, VIEW.h);
  d.each((x, y) => {
    if (y >= GAP.top && y <= 44 && x >= 9 && x <= 138) return lit(x, y, 1 - Math.abs(x - 74) / 80) ? GOLD : undefined;
    if (y < 47) return undefined;
    const v = Math.min(1, Math.max(0, 1 - Math.hypot((x - 74) / 76, (y - 47) / 24)) * 1.3);
    return base.get(x, y) === INK && (y - 48) % 7 !== 6 && lit(x, y, v) ? GOLD : undefined;
  });
  return d;
}

// ---------- the envelope ----------
function seal(d, cx, cy, r2, body = PAPER) { // the seal: an ink disc with the door mark in it (no letters)
  for (let y = -5; y <= 5; y++) for (let x = -5; x <= 5; x++) if (x * x + y * y <= r2) d.set(cx + x, cy + y, INK);
  d.rect(cx - 1, cy - 2, 3, 5, body === GOLD ? GOLD : PAPER); d.set(cx + 1, cy, INK);
}
const fillOf = tier => (tier === 'jackpot' ? GOLD : PAPER);
// closed, lying on the floor, drawn in the ENV box. Plain (points, shield): 36 x 22 in the box's bottom middle, the last row and column its
// shadow. Big: the whole box, 44 x 28: a two-pixel ink edge, a stitched border, a thick card's edge seen side-on and a deeper shadow, the
// seal two art pixels wider. Gilded: the big one in gold, with glints.
export function envelope(tier = 'points') {
  const d = grid(ENV.w, ENV.h), body = fillOf(tier);
  if (tier === 'big' || tier === 'jackpot') {
    const bw = 43, bh = 25, ax = 21, ay = 13;
    d.rect(0, 0, bw, bh, body);
    d.box(0, 0, bw, bh); d.box(1, 1, bw - 2, bh - 2); // the heavy edge
    for (let x = 4; x < bw - 4; x += 2) { d.set(x, 3, INK); d.set(x, bh - 4, INK); } // the stitches
    for (let y = 5; y < bh - 4; y += 2) { d.set(3, y, INK); d.set(bw - 4, y, INK); }
    d.rect(1, bh, bw, ENV.h - bh, INK); d.rect(bw, 1, 1, ENV.h - 1, INK); // the shadow, three rows deep
    for (let x = 2; x < bw; x += 2) d.set(x, bh, PAPER); // the card's edge, seen side-on
    d.line(2, bh - 3, 16, ay + 1); d.line(bw - 3, bh - 3, 26, ay + 1); // the bottom folds
    d.line(2, 2, ax, ay); d.line(bw - 3, 2, ax, ay); d.line(3, 2, ax, ay - 1); d.line(bw - 4, 2, ax, ay - 1); // the flap, two pixels heavy
    seal(d, ax, ay, 26, body);
    if (tier === 'jackpot') for (const [x, y] of [[5, 5], [6, 5], [5, 6], [36, 18], [35, 19], [37, 19], [36, 20], [9, 19]]) d.set(x, y, PAPER);
    return d;
  }
  const s = grid(SMALL.w, SMALL.h), bw = 35, bh = 21;
  s.rect(0, 0, bw, bh, body); s.box(0, 0, bw, bh);
  s.rect(1, bh, bw, SMALL.h - bh, INK); s.rect(bw, 1, 1, SMALL.h - 1, INK); // shadow
  const apex = [17, 11];
  s.line(1, bh - 2, 12, 12); s.line(bw - 2, bh - 2, 22, 12); // the bottom folds
  s.line(1, 1, apex[0], apex[1]); s.line(bw - 2, 1, apex[0], apex[1]); s.line(2, 1, apex[0], apex[1] - 1); s.line(bw - 3, 1, apex[0], apex[1] - 1); // the flap, two pixels heavy
  seal(s, apex[0], apex[1], 10, body);
  for (let y = 0; y < SMALL.h; y++) for (let x = 0; x < SMALL.w; x++) { const v = s.get(x, y); if (v) d.set(SMALL.dx + x, SMALL.dy + y, v); }
  return d;
}

function shieldShape(d, ox, oy, w, h, face = PAPER) { // a shield with a keyhole: flat top, straight sides, a point
  const mid = (w - 1) / 2;
  for (let y = 0; y < h; y++) {
    const t = y < h * 0.55 ? 0 : (y - h * 0.55) / (h * 0.45), half = mid * (1 - t * t * 0.95);
    for (let x = 0; x < w; x++) if (Math.abs(x - mid) <= half + 0.01) d.set(ox + x, oy + y, Math.abs(Math.abs(x - mid) - half) < 1 || y === 0 ? INK : face);
  }
  const cx = ox + Math.round(mid), cy = oy + Math.round(h * 0.33);
  d.rect(cx - 1, cy - 1, 3, 3, INK); d.rect(cx, cy + 2, 1, 3, INK); d.rect(cx - 1, cy + 4, 3, 1, INK); // the keyhole
}
// the big mark: an ink starburst stamp with the door in it; the gilded mark: a gold medal with a keyhole
export function mark(kind = 'big') {
  const d = grid(MARK, MARK), c = (MARK - 1) / 2;
  d.each((x, y) => {
    const dx = x - c, dy = y - c, r = Math.hypot(dx, dy), a = Math.atan2(dy, dx);
    if (kind === 'big') {
      if (r <= 2.9) return PAPER;
      if (r <= 4.2 + 1.9 * Math.max(0, Math.cos(8 * a))) return INK;
      return undefined;
    }
    if (r > 5.9) return undefined;
    if (r > 4.9) return INK;
    if (r > 3.9 && a > -2.6 && a < -1.2) return PAPER; // the shine
    return GOLD;
  });
  if (kind === 'big') { d.rect(5, 4, 2, 4, INK); d.set(6, 6, PAPER); }
  else { d.rect(5, 3, 2, 2, INK); d.rect(5, 5, 2, 1, INK); d.rect(4, 6, 4, 2, INK); }
  return d;
}

// opened, after the zoom: 36 x 40, the body in rows 18..39 (the closed sprite's rows 0..21), the flap up behind, the letter (or the
// shield) half out, the seal cracked in two on the pocket.
export function opened(tier = 'points') {
  const d = grid(OPEN.w, OPEN.h), body = fillOf(tier), top = 18, bw = 35, bh = 21;
  for (let y = 4; y < top; y++) { const half = Math.round(17 * (y - 4) / (top - 4)); d.rect(17 - half, y, half * 2 + 1, 1, body); }
  d.line(0, top, 17, 4); d.line(bw - 1, top, 17, 4); // the flap folded up behind
  d.rect(0, top, bw, bh, body); d.box(0, top, bw, bh);
  for (let y = top + 1; y < top + 12; y++) for (let x = 1; x < bw - 1; x++) if ((x + y) % 2 === 0) d.set(x, y, INK); // the dark inside
  if (tier === 'shield') shieldShape(d, 9, 1, 17, 26);
  else {
    const lx = 6, ly = 1, lw = 23, lh = 28;
    d.rect(lx, ly, lw, lh, PAPER); d.box(lx, ly, lw, lh, INK);
    if (tier === 'big' || tier === 'jackpot') d.box(lx + 1, ly + 1, lw - 2, lh - 2, tier === 'jackpot' ? GOLD : INK); // a heavy edge; gilded, a gold one
    for (const [y, w] of [[6, 13], [9, 16], [12, 10], [15, 14]]) for (let x = lx + 4; x < lx + 4 + w; x += 2) d.set(x, ly + y, INK); // lines of writing
    if (tier === 'big' || tier === 'jackpot') { const m = mark(tier === 'big' ? 'big' : 'gilded'); for (let y = 0; y < MARK; y++) for (let x = 0; x < MARK; x++) { const v = m.get(x, y); if (v) d.set(lx + lw - MARK - 1 + x, ly + 2 + y, v); } }
  }
  // the pocket in front: below the V from the top corners to the middle
  const vAt = x => top + 1 + 11 * (x <= 17 ? (x - 1) / 16 : (bw - 2 - x) / 16);
  for (let x = 1; x < bw - 1; x++) for (let y = Math.ceil(vAt(x)); y < top + bh - 1; y++) d.set(x, y, body);
  d.line(1, top + 1, 17, top + 12); d.line(bw - 2, top + 1, 17, top + 12);
  d.line(1, top + bh - 2, 12, top + 12); d.line(bw - 2, top + bh - 2, 22, top + 12);
  d.box(0, top, bw, bh);
  d.rect(1, top + bh, bw, 1, INK); d.rect(bw, top + 1, 1, bh, INK); // shadow
  // the seal, cracked: two halves pulled apart
  for (let y = -2; y <= 2; y++) for (let x = -2; x <= 2; x++) if (x * x + y * y <= 5) { if (x <= 0 && y <= x + 1) d.set(14 + x, top + 12 + y, INK); if (x >= 0 && y >= x - 1) d.set(21 + x, top + 14 + y, INK); }
  d.set(17, top + 16, INK); d.set(19, top + 18, INK); // crumbs of wax
  if (tier === 'jackpot') for (const [x, y] of [[3, top + 16], [4, top + 17], [31, top + 15]]) d.set(x, y, PAPER);
  return d;
}

// ---------- the peephole: everything dark but a round lens; the bezel knurled, paper or gold ----------
// r: the lens radius (the iris's frames are drawn wider, never scaled); grain: the door's grain round it (the iris's 67 ms frames go
// without, plain ink, to keep the page light)
export function hole(gold = false, r = LENS.r, grained = true) {
  const d = grid(VIEW.w, VIEW.h), { cx, cy } = LENS, ring = gold ? GOLD : PAPER;
  d.each((x, y) => {
    const dx = x + 0.5 - cx, dy = y + 0.5 - cy, dist = Math.hypot(dx, dy), a = Math.atan2(dy, dx);
    if (dist < r) return 0;
    if (dist < r + 1) return INK;
    if (dist < r + 4) {
      if (dist >= r + 3 && Math.floor((a + Math.PI) / (Math.PI / 16)) % 2 === 0) return INK; // the knurled outer edge
      if (dist < r + 2 && a < -1.4 && a > -2.9) return gold ? PAPER : INK; // the bezel's shine (paper on gold) or its inner shadow
      return ring;
    }
    if (dist < r + 5 || !grained) return INK;
    const grain = (x * 7 + Math.floor(Math.sin(y * 0.05 + x) * 3)) % 11 === 0 ? 0.12 : 0;
    return lit(x, y, 0.05 + grain) ? PAPER : INK; // the door round it, nearly dark, its grain
  });
  return d;
}

// behind the opened envelope (the view is zoomed to 3 px per art px then, so only x 24..123, y 32..83 shows): points and shield, a quiet pool of
// light round the letter; big, a burst of paper rays; gilded, gold rays with paper sparkles
export const GLOW = { cx: 74, cy: 50 };
export function glow(tier = 'points') {
  const d = grid(VIEW.w, VIEW.h), gold = tier === 'jackpot', burst = tier === 'big' || gold;
  d.each((x, y) => {
    const dx = x + 0.5 - GLOW.cx, dy = y + 0.5 - GLOW.cy, a = Math.atan2(dy, dx), r = Math.hypot(dx, dy);
    if (!burst) return lit(x, y, 0.08 + 0.9 * Math.max(0, 1 - r / 34)) ? PAPER : INK;
    if (r < 6) return gold ? GOLD : PAPER;
    const on = Math.floor((a + Math.PI) / (Math.PI / 9) + r / 60) % 2 === 0;
    return on ? (gold ? GOLD : PAPER) : INK;
  });
  if (gold) for (const [x, y] of [[44, 37], [104, 39], [46, 57], [102, 58], [42, 70], [106, 69]]) sparkle(d, x, y);
  return d;
}
function sparkle(d, x, y) { // a four-point paper star in an ink halo
  for (const [i, j] of [[-3, 0], [3, 0], [0, -3], [0, 3], [-2, -1], [-2, 1], [2, -1], [2, 1], [-1, -2], [1, -2], [-1, 2], [1, 2], [-1, -1], [1, -1], [-1, 1], [1, 1]]) d.set(x + i, y + j, INK);
  for (const [i, j] of [[0, 0], [-2, 0], [-1, 0], [1, 0], [2, 0], [0, -2], [0, -1], [0, 1], [0, 2]]) d.set(x + i, y + j, PAPER);
}

// The big and gilded results burst out of the window (BURST, drawn at the stage's 2x): rays from the opened envelope out to the box's
// edges, two lit rays to each ink wedge, an ink rim so the burst reads against the room. Big: paper rays. Gilded: gold rays
// and paper sparkles, in the biggest box.
export function burst(tier = 'big') {
  const b = BURST[tier], w = b.w / ART_PX, h = b.h / ART_PX, gold = tier === 'jackpot', N = 24;
  const o = place(OPEN, ZOOM.p, ZOOM.x, ZOOM.y);
  const cx = (o.left + o.width / 2 - b.x) / ART_PX, cy = (WIN.title + o.top + o.height / 2 - b.y) / ART_PX;
  const d = grid(w, h);
  d.each((x, y) => {
    const dx = x + 0.5 - cx, dy = y + 0.5 - cy, a = Math.atan2(dy, dx), r = Math.hypot(dx, dy);
    const k = Math.min(N - 1, Math.floor((a + Math.PI) / (2 * Math.PI / N))), ux = Math.cos(a), uy = Math.sin(a);
    const edge = Math.min(ux > 0 ? (w - cx) / ux : ux < 0 ? -cx / ux : Infinity, uy > 0 ? (h - cy) / uy : uy < 0 ? -cy / uy : Infinity);
    const on = k % 3 !== 2, reach = edge * b.reach[on ? k % 3 : 2]; // two lit rays (long, then shorter) to one ink wedge
    if (r > reach) return undefined;
    return on ? (gold ? GOLD : PAPER) : INK;
  });
  const g = Uint8Array.from(d.g);
  const at = (x, y) => (x >= 0 && y >= 0 && x < w && y < h ? g[y * w + x] : 0);
  d.each((x, y, v) => (v && (!at(x - 1, y) || !at(x + 1, y) || !at(x, y - 1) || !at(x, y + 1)) ? INK : undefined));
  if (gold) for (const [x, y] of [[8, 9], [40, 7], [158, 8], [188, 12], [10, 62], [186, 70], [14, 104], [182, 108]]) sparkle(d, x, y);
  return d;
}

// a Bayer veil tile (8 x 8): the room's flash (paper) and the light's dip (ink)
export function veil(v, colour = INK) { const d = grid(8, 8); d.each((x, y) => (lit(x, y, v) ? colour : 0)); return d; }

// ---------- the tray icons: paper drawn on an ink tile, 12 x 12 ----------
// the lamp, out (0) to full (10): a bulb on its cord; steps 1 to 9 light its nine rays one at a time, from the bottom up both sides,
// and the tenth wraps it in a halo
const RAYS = [0, -36, 36, -72, 72, -108, 108, -144, 144].map(deg => {
  const a = deg * Math.PI / 180;
  return [4.6, 5.6].map(r => [Math.max(0, Math.min(ICON - 1, Math.round(6 + r * Math.sin(a)))), Math.max(0, Math.min(ICON - 1, Math.round(6.2 + r * Math.cos(a))))]);
});
export function lampIcon(step) {
  const d = grid(ICON, ICON); d.rect(0, 0, ICON, ICON, INK);
  const on = Math.max(0, Math.min(STEPS, step));
  d.rect(5, 0, 2, 2, PAPER); d.rect(4, 2, 4, 1, PAPER); // the cord and the cap
  for (let y = 3; y <= 9; y++) for (let x = 3; x <= 9; x++) { // the bulb: an outline when out, solid when lit
    const r = Math.hypot(x - 6, y - 6.2);
    if (r <= 3.2) d.set(x, y, on || r > 2.3 ? PAPER : INK);
  }
  if (!on) d.rect(5, 5, 3, 1, PAPER); // out: the filament only
  if (on >= STEPS) d.each((x, y, v) => (v === INK && Math.hypot(x - 6, y - 6.2) < 4.4 && (x + y) % 2 === 0 ? PAPER : undefined)); // full: a halo
  RAYS.slice(0, on).forEach(px => { for (const [x, y] of px) d.set(Math.min(ICON - 1, x), y, PAPER); });
  return d;
}
// the keyring: a ring of ten fragments, the held ones solid, the missing ones a dotted outline
export function keyringIcon(n) {
  const d = grid(ICON, ICON); d.rect(0, 0, ICON, ICON, INK);
  d.each((x, y) => {
    const dx = x + 0.5 - 6, dy = y + 0.5 - 6, r = Math.hypot(dx, dy);
    if (r < 2.2 || r > 6) return undefined;
    const sector = Math.floor(((Math.atan2(dx, -dy) + 2 * Math.PI) % (2 * Math.PI)) / (2 * Math.PI / STEPS)); // clockwise from the top
    if (sector < n) return PAPER;
    return r > 4.8 ? PAPER : undefined; // a missing fragment: only the ring's thin rim
  });
  return d;
}
// The Big Knock's keyring (sprint 29 fix pass, art): the tray chip's ring drawn large enough to count. RING x RING art px (2x on screen): a split
// ring hanging from a loop, ten key tags round it clockwise from the top right; a fragment you have is an ink tag with a paper keyhole, a
// missing one a dotted empty tag. Ink and paper only; see-through round the drawing. slots(): the tags' centres, for the tests.
export const RING = 70;
export const ringSlots = () => Array.from({ length: STEPS }, (_, i) => { const a = (i + 0.5) * 2 * Math.PI / STEPS; return { x: Math.round(35 + 26 * Math.sin(a)), y: Math.round(37 - 26 * Math.cos(a)) }; });
export function keyringRing(have) {
  const d = grid(RING, RING), n = Math.max(0, Math.min(STEPS, Math.floor(have) || 0));
  d.each((x, y) => { const r = Math.hypot(x + 0.5 - 35, y + 0.5 - 37); return r >= 24.6 && r < 27.4 ? INK : undefined; }); // the ring, 3 px
  d.rect(32, 1, 7, 2, INK); d.rect(32, 1, 2, 10, INK); d.rect(37, 1, 2, 10, INK); // the loop it hangs from
  ringSlots().forEach(({ x, y }, i) => {
    const x0 = x - 3, y0 = y - 3;
    d.rect(x0 - 1, y0 - 1, 9, 9, PAPER); // a paper gap where the ring runs behind the tag
    if (i < n) {
      d.rect(x0, y0, 7, 7, INK);
      d.rect(x0 + 2, y0 + 1, 3, 3, PAPER); d.rect(x0 + 3, y0 + 4, 1, 2, PAPER); // the keyhole
    } else {
      for (let k = 0; k < 7; k += 2) { d.set(x0 + k, y0, INK); d.set(x0 + k, y0 + 6, INK); d.set(x0, y0 + k, INK); d.set(x0 + 6, y0 + k, INK); }
    }
  });
  return d;
}
export function shieldIcon() { const d = grid(ICON, ICON); d.rect(0, 0, ICON, ICON, INK); shieldShape(d, 2, 1, 8, 10); return d; }

// the step a tray line shows: "Lamp x1.4" -> 4, "Lamp out" -> 0; "Keyring 3/10" -> 3
export const lampStep = text => { const m = /x(\d+(?:\.\d+)?)/.exec(text); return m ? Math.max(0, Math.min(STEPS, Math.round((Number(m[1]) - 1) * 10))) : 0; };
export const keyringStep = text => { const m = /(\d+)\s*\/\s*10/.exec(text); return m ? Math.max(0, Math.min(STEPS, Number(m[1]))) : 0; };

// all the sprites as CSS variables, drawn once
let SPRITES = null;
export function sprites() {
  if (SPRITES) return SPRITES;
  const base = scene();
  SPRITES = {
    '--m-scene': svgUrl(base), '--m-gold': svgUrl(seep(base)),
    '--m-env': svgUrl(envelope('points')), '--m-env-big': svgUrl(envelope('big')), '--m-env-gold': svgUrl(envelope('jackpot')),
    '--m-open': svgUrl(opened('points')), '--m-open-shield': svgUrl(opened('shield')), '--m-open-big': svgUrl(opened('big')), '--m-open-gold': svgUrl(opened('jackpot')),
    '--m-hole': svgUrl(hole(false)), '--m-hole-big': svgUrl(hole(false, LENS_BIG)), '--m-hole-gold': svgUrl(hole(true, LENS_BIG)),
    '--m-glow': svgUrl(glow('points')), '--m-rays': svgUrl(glow('big')), '--m-rays-gold': svgUrl(glow('jackpot')),
    '--m-burst-big': svgUrl(burst('big')), '--m-burst-gold': svgUrl(burst('jackpot')),
    '--m-mark-big': svgUrl(mark('big')), '--m-mark-gold': svgUrl(mark('gilded')), '--m-shield': svgUrl(shieldIcon()),
    '--m-dip': svgUrl(veil(0.5, INK)), '--m-flash': svgUrl(veil(0.75, PAPER)),
  };
  // the iris: three drawn frames per lens, a, b, c, each the lens wider by IRIS[i] (plain, big, gilded)
  IRIS.forEach((w, i) => {
    const k = 'abc'[i];
    SPRITES[`--m-iris-${k}`] = svgUrl(hole(false, LENS.r + w, false));
    SPRITES[`--m-iris-big-${k}`] = svgUrl(hole(false, LENS_BIG + w, false));
    SPRITES[`--m-iris-gold-${k}`] = svgUrl(hole(true, LENS_BIG + w, false));
  });
  SPRITES.lamp = Array.from({ length: STEPS + 1 }, (_, i) => svgUrl(lampIcon(i)));
  SPRITES.keyring = Array.from({ length: STEPS + 1 }, (_, i) => svgUrl(keyringIcon(i)));
  return SPRITES;
}

// ---------- the parts ----------
const part = (doc, tag, cls, text = '') => { const e = doc.createElement(tag); e.className = cls; if (text) e.textContent = text; return e; };
const setVar = (e, k, v) => e.style?.setProperty?.(k, v);
// mail.js writes a tray line's words; this keeps the element's data-step and icon in step with them (works on a plain object too)
// then(e, step): called after each write (the lamp's label, below)
export function mirror(e, stepOf, icons, then) {
  let d = null;
  for (let p = Object.getPrototypeOf(e); p && !d; p = Object.getPrototypeOf(p)) d = Object.getOwnPropertyDescriptor(p, 'textContent') ?? null;
  let own = e.textContent ?? '';
  Object.defineProperty(e, 'textContent', {
    configurable: true,
    get() { return d ? d.get.call(e) : own; },
    set(v) { if (d) d.set.call(e, v); else own = v; const s = stepOf(String(v ?? '')); e.dataset.step = String(s); if (icons) setVar(e, '--m-icon', icons[s]); then?.(e, s); },
  });
  return e;
}

// One streak face (sprint 28 fix, coordinator's ruling): the door's chalk keeps its own "x2" for door points; the lamp only multiplies
// envelope points. So the first time a lit lamp shows, its chip says so ("Lamp x1.4: envelope points") for as long as that tray is up
// (LAMP_LABEL_MS); from then on, in this browser, the chip is the lamp icon alone (its words stay for screen readers), so two bare
// "x" numbers never sit side by side. Before the lamp has ever been lit the chip keeps its words ("Lamp out").
// lampLabel(step) -> 'label' | 'short' | 'long'.
export const LAMP_LABEL = 'envelope points', LAMP_KEY = 'door-lamp-label', LAMP_LABEL_MS = 15000;
let lampMem = null;
export const lampStore = {
  get() { try { return globalThis.localStorage?.getItem(LAMP_KEY) ?? lampMem; } catch { return lampMem; } },
  set(v) { lampMem = v; try { globalThis.localStorage?.setItem(LAMP_KEY, v); } catch { /* private mode: this page only */ } },
};
export function lampLabeller({ now = () => Date.now(), store = lampStore } = {}) {
  let since = null;
  return step => {
    if (since !== null && now() - since < LAMP_LABEL_MS) return 'label';
    if (step > 0 && !store.get()) { since = now(); store.set(String(since)); return 'label'; }
    return store.get() ? 'short' : 'long';
  };
}
function labelLamp(labeller) {
  return (e, step) => {
    const look = labeller(step);
    if (look === 'label') e.dataset.label = LAMP_LABEL; else delete e.dataset.label;
    if (look === 'short') e.dataset.short = '1'; else delete e.dataset.short;
  };
}

export function buildMailParts(doc, { labeller = lampLabeller() } = {}) {
  const art = sprites();
  const tray = part(doc, 'div', 'mail-tray'), stage = part(doc, 'div', 'mail-stage');
  const lamp = mirror(part(doc, 'span', 'mail-lamp'), lampStep, art.lamp, labelLamp(labeller)), keyring = mirror(part(doc, 'span', 'mail-keyring'), keyringStep, art.keyring);
  const shield = part(doc, 'span', 'mail-shield'), last = part(doc, 'span', 'mail-last'), stamp = part(doc, 'i', 'mail-mark'), session = part(doc, 'span', 'mail-session');
  tray.append(lamp, keyring, stamp, session, shield, last);
  const flash = part(doc, 'div', 'mail-flash'), title = part(doc, 'div', 'mail-title'), titleText = part(doc, 'span', 'mail-title-t', 'Under the door');
  title.append(titleText);
  const sceneBox = part(doc, 'div', 'mail-scene'), view = part(doc, 'div', 'mail-view');
  const gold = part(doc, 'div', 'mail-gold'), glow = part(doc, 'div', 'mail-rays'), flicker = part(doc, 'div', 'mail-flicker');
  const hole = part(doc, 'div', 'mail-hole'), paper = part(doc, 'div', 'mail-paper'), result = part(doc, 'div', 'mail-result'), hint = part(doc, 'div', 'mail-hint', 'Tap to skip');
  const rect = part(doc, 'div', 'mail-zoomrect'), burstBox = part(doc, 'div', 'mail-burst'), plate = part(doc, 'div', 'mail-plate');
  view.append(gold, glow, paper, flicker, hole);
  sceneBox.append(view, rect);
  result.setAttribute('role', 'status'); result.setAttribute('aria-live', 'polite');
  stage.append(flash, burstBox, title, sceneBox, plate, result, hint);
  for (const [k, v] of Object.entries(art)) if (k.startsWith('--')) setVar(stage, k, v);
  for (const k of ['--m-mark-big', '--m-mark-gold', '--m-shield']) setVar(tray, k, art[k]); // the tray needs only these
  return { tray, stage, lamp, keyring, shield, session, last, hole, paper, result, hint };
}

// The tray lines. Plain words: no promises, no money.
export const lampText = lamp => (lamp.n > 0 ? `Lamp x${lamp.mult}` : 'Lamp out');
export const keyringText = n => `Keyring ${n}/10`;
export const shieldText = n => (n > 0 ? 'Shield on' : '');
// The free room's running score (the streak is read off the chalk on the door, once: sprint 31), kept in the tray so a phone shows it during play (sprint 30 item 3 fix pass); the night room sends no session
export const sessionText = s => (s && (s.points || s.streak) ? `Session ${s.points}` : '');
